import { Icona } from '../../components/Icona';
import { useEffect, useState } from 'react';
import type { CommessaAperta } from '../../api/commesse';
import { formattaData, formattaNumero, oggiIso } from '../../lib/formato';
import { coda } from './coda';
import { commesseInCantiere } from './commesseInCantiere';
import type {
  RapportinoLocale,
  RigaMaterialeLocale,
  RigaOreLocale,
  RigaTrasfertaLocale,
} from './tipi';

/*  Le commesse arrivano dal database, non da un elenco scritto qui.

    Prima erano tre righe di esempio con identificativi inventati, e non era un dettaglio
    estetico: quegli identificativi non esistono nella tabella delle commesse, quindi il
    primo rapportino sincronizzato veniva rifiutato dalla chiave esterna e restava in
    coda per sempre — la coda offline non aveva mai consegnato niente, e questo era il
    motivo.  */
const PASSO_ORE = 0.25;

/*  `operaioId` arriva dalla sessione e non da una costante: è chi ha lavorato, e finisce
    dentro il rapportino prima che ci sia rete. Per questo la sessione si tiene da parte
    nel browser — senza sapere chi è collegato, in cantiere non si potrebbe nemmeno
    cominciare a compilare.  */
export function NuovoRapportino({
  operaioId,
  onSalvato,
}: {
  operaioId: string;
  onSalvato: (rimasti: number) => void;
}) {
  const [data, setData] = useState(oggiIso());
  const [ore, setOre] = useState<RigaOreLocale[]>([]);
  const [trasferte, setTrasferte] = useState<RigaTrasfertaLocale[]>([]);
  const [materiali, setMateriali] = useState<RigaMaterialeLocale[]>([]);
  const [note, setNote] = useState('');
  const [messaggio, setMessaggio] = useState<string | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [commesse, setCommesse] = useState<CommessaAperta[] | null>(null);
  const [origineElenco, setOrigineElenco] = useState<string | null>(null);
  const [erroreElenco, setErroreElenco] = useState(false);

  useEffect(() => {
    const aggiorna = () => setOnline(navigator.onLine);
    window.addEventListener('online', aggiorna);
    window.addEventListener('offline', aggiorna);
    return () => {
      window.removeEventListener('online', aggiorna);
      window.removeEventListener('offline', aggiorna);
    };
  }, []);

  /*  L'elenco si ricarica quando torna il campo: in cantiere si apre l'applicazione
      offline, e appena c'e' un attimo di rete conviene avere i numeri aggiornati.  */
  useEffect(() => {
    let annullato = false;

    commesseInCantiere()
      .then((c) => {
        if (annullato) return;
        setCommesse(c.elenco);
        setOrigineElenco(c.dallaCache && c.scaricatoIl ? c.scaricatoIl : null);
        setErroreElenco(false);
      })
      .catch(() => !annullato && setErroreElenco(true));

    return () => {
      annullato = true;
    };
  }, [online]);

  const oreTotali = ore.reduce((somma, riga) => somma + riga.ore, 0);
  const kmTotali = trasferte.reduce((somma, riga) => somma + riga.km, 0);

  function aggiungiCommessa(id: string) {
    const commessa = commesse?.find((c) => c.id === id);
    if (!commessa || ore.some((o) => o.commessaId === id)) return;

    const numeroCommessa = commessa.numero ?? 'senza numero';
    setOre((precedenti) => [...precedenti, { commessaId: id, numeroCommessa, ore: 0 }]);

    /*  I chilometri si propongono da soli, per intero, appena si aggiunge la commessa: chi
        compila in cantiere non deve ricordarsi quanti sono, e chi li ricorda a memoria
        sbaglia. Restano modificabili — la strada di oggi può essere stata un'altra — e
        azzerarli vuol dire «oggi non ci sono andato».  */
    if (commessa.kmTrasferta !== null && commessa.kmTrasferta > 0) {
      setTrasferte((precedenti) => [
        ...precedenti,
        { commessaId: id, numeroCommessa, km: commessa.kmTrasferta! },
      ]);
    }
  }

  function togliCommessa(id: string) {
    setOre((precedenti) => precedenti.filter((o) => o.commessaId !== id));
    setTrasferte((precedenti) => precedenti.filter((t) => t.commessaId !== id));
  }

  function cambiaKm(id: string, km: number) {
    setTrasferte((precedenti) => {
      if (precedenti.some((t) => t.commessaId === id)) {
        return precedenti.map((t) => (t.commessaId === id ? { ...t, km } : t));
      }
      const riga = ore.find((o) => o.commessaId === id);
      return riga ? [...precedenti, { commessaId: id, numeroCommessa: riga.numeroCommessa, km }] : precedenti;
    });
  }

  function cambiaOre(id: string, delta: number) {
    setOre((precedenti) =>
      precedenti.map((riga) =>
        riga.commessaId === id
          ? { ...riga, ore: Math.max(0, Math.round((riga.ore + delta) / PASSO_ORE) * PASSO_ORE) }
          : riga,
      ),
    );
  }

  async function salva() {
    const righeConOre = ore.filter((o) => o.ore > 0);

    if (righeConOre.length === 0) {
      setMessaggio('Segna almeno un’ora su una commessa.');
      return;
    }
    if (oreTotali > 16) {
      setMessaggio(`${oreTotali} ore in una giornata: controlla, sembra un errore.`);
      return;
    }

    const rapportino: RapportinoLocale = {
      // L'identificativo nasce qui, sul telefono, prima di sapere se c'è rete:
      // è ciò che rende l'invio ripetibile senza creare doppioni.
      idDispositivo: crypto.randomUUID(),
      operaioId,
      dataLavoro: data,
      compilatoIlDispositivo: new Date().toISOString(),
      note: note.trim() || undefined,
      ore: righeConOre,
      materiali,
      /*  Solo le trasferte su una commessa su cui si sono segnate ore, e solo con
          chilometri veri: zero vuol dire «non ci sono andato», e il dominio rifiuterebbe
          la riga facendo tornare indietro tutto il rapportino.  */
      trasferte: trasferte.filter(
        (t) => t.km > 0 && righeConOre.some((o) => o.commessaId === t.commessaId),
      ),
      stato: 'in-attesa',
      tentativi: 0,
    };

    await coda.accoda(rapportino);

    const esito = await coda.sincronizza();
    onSalvato(esito.rimasti);

    setMessaggio(
      esito.inviati > 0
        ? 'Rapportino inviato.'
        : 'Rapportino salvato sul telefono: parte da solo quando torna il campo.',
    );

    setOre([]);
    setTrasferte([]);
    setMateriali([]);
    setNote('');
  }

  return (
    <div className="rapportino">
      <header className="testata-mobile">
        <h1>Rapportino di lavoro</h1>
        <span className={online ? 'stato online' : 'stato offline'}>
          {online ? 'in linea' : 'senza campo'}
        </span>
      </header>

      <label className="campo-blocco">
        <span>Giornata</span>
        <input type="date" value={data} max={oggiIso()} onChange={(e) => setData(e.target.value)} />
      </label>

      <section>
        <h2>Ore per commessa</h2>

        {ore.map((riga) => (
          <div key={riga.commessaId} className="riga-ore">
            <div className="chi">
              <strong>{riga.numeroCommessa}</strong>
              <small>
                {commesse?.find((c) => c.id === riga.commessaId)?.descrizione}
              </small>
            </div>
            {/* Pulsanti larghi e passo di un quarto d'ora: si compila con i guanti da lavoro. */}
            <div className="contatore">
              {/*  L'etichetta letta ad alta voce dice anche di che cosa: «meno un quarto
                   d'ora su 26-0001». Con dieci commesse in elenco, dieci bottoni che
                   dicono tutti «meno» non servono a niente.  */}
              <button
                type="button"
                aria-label={`Un quarto d'ora in meno su ${riga.numeroCommessa}`}
                onClick={() => cambiaOre(riga.commessaId, -PASSO_ORE)}
              >
                <Icona nome="meno" />
              </button>
              <output>{riga.ore.toFixed(2).replace('.', ',')}</output>
              <button
                type="button"
                aria-label={`Un quarto d'ora in piu' su ${riga.numeroCommessa}`}
                onClick={() => cambiaOre(riga.commessaId, PASSO_ORE)}
              >
                <Icona nome="piu" />
              </button>
            </div>
            <button
              type="button"
              className="rimuovi"
              aria-label={`Togli ${riga.numeroCommessa}`}
              onClick={() => togliCommessa(riga.commessaId)}
            >
              <Icona nome="rimuovi" />
            </button>
          </div>
        ))}

        <select
          className="aggiungi"
          value=""
          onChange={(e) => {
            aggiungiCommessa(e.target.value);
            e.currentTarget.value = '';
          }}
        >
          <option value="">+ Aggiungi commessa…</option>
          {(commesse ?? [])
            .filter((c) => !ore.some((o) => o.commessaId === c.id))
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.numero} — {c.descrizione}
              </option>
            ))}
        </select>

        {/*  Le tre condizioni vanno distinte, perche' mandano l'operaio in tre posti
             diversi: nessuna commessa aperta e' un problema dell'ufficio, un elenco che
             non si e' mai scaricato si risolve con un minuto di rete, e un elenco vecchio
             va solo saputo.  */}
        {erroreElenco && (
          <p className="messaggio">
            Elenco commesse non disponibile: apri l'applicazione una volta dove c'è campo,
            così resta salvato sul telefono anche per i cantieri senza rete.
          </p>
        )}
        {commesse?.length === 0 && (
          <p className="messaggio">
            Nessuna commessa aperta: chiedi all'ufficio di aprirla prima di segnare le ore.
          </p>
        )}
        {origineElenco && (
          <p className="nota">
            Elenco commesse del {formattaData(origineElenco)}: senza campo si usa l'ultima
            copia scaricata.
          </p>
        )}

        <p className="totale-ore">
          Totale giornata <strong>{oreTotali.toFixed(2).replace('.', ',')}</strong> ore
        </p>
      </section>

      {ore.length > 0 && (
        <section className="trasferte">
          <h2>Trasferte</h2>
          {/*  Una riga per ogni commessa della giornata, con i chilometri già scritti dove
               l'ufficio li ha stabiliti. Si correggono e si azzerano: zero vuol dire che
               oggi in quel cantiere non ci si è andati.  */}
          {ore.map((riga) => {
            const trasferta = trasferte.find((t) => t.commessaId === riga.commessaId);
            const proposti = commesse?.find((c) => c.id === riga.commessaId)?.kmTrasferta ?? null;

            return (
              <div key={riga.commessaId} className="riga-trasferta">
                <div className="chi">
                  <strong>{riga.numeroCommessa}</strong>
                  <small>
                    {proposti === null
                      ? 'chilometri non stabiliti dall’ufficio'
                      : `${formattaNumero(proposti)} km previsti`}
                  </small>
                </div>
                <label className="km">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={1200}
                    step={1}
                    aria-label={`Chilometri per ${riga.numeroCommessa}`}
                    value={trasferta && trasferta.km > 0 ? String(trasferta.km) : ''}
                    placeholder="0"
                    onChange={(e) => {
                      const km = Number(e.target.value);
                      cambiaKm(riga.commessaId, Number.isFinite(km) && km > 0 ? km : 0);
                    }}
                  />
                  <span>km</span>
                </label>
              </div>
            );
          })}

          <p className="totale-ore">
            Totale trasferte <strong>{formattaNumero(kmTotali)}</strong> km
          </p>
        </section>
      )}

      <section>
        <h2>Materiale usato</h2>
        <MaterialeUsato
          commesse={ore.map((o) => ({ id: o.commessaId, numero: o.numeroCommessa }))}
          righe={materiali}
          onCambia={setMateriali}
        />
      </section>

      <label className="campo-blocco">
        <span>Note</span>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>

      {messaggio && <p className="messaggio">{messaggio}</p>}

      <button type="button" className="salva" onClick={salva}>
        Salva e invia
      </button>
    </div>
  );
}

function MaterialeUsato({
  commesse,
  righe,
  onCambia,
}: {
  commesse: { id: string; numero: string }[];
  righe: RigaMaterialeLocale[];
  onCambia: (righe: RigaMaterialeLocale[]) => void;
}) {
  const [descrizione, setDescrizione] = useState('');
  const [quantita, setQuantita] = useState('');
  const [unita, setUnita] = useState('pz');
  const [commessaId, setCommessaId] = useState('');

  if (commesse.length === 0) {
    return <p className="nota">Aggiungi prima una commessa: il materiale va imputato a una di quelle.</p>;
  }

  function aggiungi() {
    const numeroQuantita = Number(quantita.replace(',', '.'));
    const commessa = commesse.find((c) => c.id === (commessaId || commesse[0]!.id));
    if (!descrizione.trim() || !Number.isFinite(numeroQuantita) || numeroQuantita <= 0 || !commessa) return;

    onCambia([
      ...righe,
      {
        commessaId: commessa.id,
        numeroCommessa: commessa.numero,
        descrizione: descrizione.trim(),
        quantita: numeroQuantita,
        unitaMisura: unita,
      },
    ]);

    setDescrizione('');
    setQuantita('');
  }

  return (
    <>
      {righe.map((riga, indice) => (
        <div key={`${riga.descrizione}-${indice}`} className="riga-materiale">
          <span className="desc">{riga.descrizione}</span>
          <span className="qta">
            {riga.quantita.toString().replace('.', ',')} {riga.unitaMisura}
          </span>
          <span className="commessa">{riga.numeroCommessa}</span>
          <button
            type="button"
            className="rimuovi"
            aria-label="Togli materiale"
            onClick={() => onCambia(righe.filter((_, i) => i !== indice))}
          >
            <Icona nome="rimuovi" />
          </button>
        </div>
      ))}

      <div className="nuovo-materiale">
        {/* In cantiere il codice articolo spesso non c'è: si descrive a parole
            e l'ufficio lo codifica dopo. Bloccare qui significa non ricevere il dato. */}
        <input
          placeholder="Cosa hai usato"
          value={descrizione}
          onChange={(e) => setDescrizione(e.target.value)}
        />
        <input
          placeholder="Q.tà"
          inputMode="decimal"
          value={quantita}
          onChange={(e) => setQuantita(e.target.value)}
        />
        <select value={unita} onChange={(e) => setUnita(e.target.value)}>
          {['pz', 'm', 'kg', 'lt', 'corpo'].map((u) => (
            <option key={u}>{u}</option>
          ))}
        </select>
        {commesse.length > 1 && (
          <select value={commessaId} onChange={(e) => setCommessaId(e.target.value)}>
            {commesse.map((c) => (
              <option key={c.id} value={c.id}>
                {c.numero}
              </option>
            ))}
          </select>
        )}
        <button type="button" onClick={aggiungi}>
          Aggiungi
        </button>
      </div>
    </>
  );
}
